'use client';

import { useState, useEffect } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';

const HeroSection = () => {
  const { language } = useLanguage();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [scrollY, setScrollY] = useState(0);
  const isRTL = language === 'ar';

  // Background slides - using uploaded Riyadh image and placeholder URLs for law office/courtroom
  const backgroundSlides = [
    '/assets/images/saifaldhaher-vAkHAP27QMk-unsplash-1763962583881.jpg', // Riyadh skyline (uploaded image)
    'https://images.unsplash.com/photo-1497366216548-37526070297c?q=80&w=2069', // Law office interior (no people)
    'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?q=80&w=2070' // Courtroom scene (no people)
  ];

  // Motivational captions cycling with slides
  const motivationalCaptions = {
    ar: [
      'نسعى لرفع صوت الحق وإعلاء قيمة العدالة',
      'نصنع الفرق عبر تمثيل قانوني مهني وموثوق',
      'نرافق عملاءنا بخطوات ثابتة نحو الحل الأمثل',
      'قانونياً… أنت لست وحدك، نحن معك في كل خطوة'
    ],
    en: [
      'We strive to raise the voice of justice and uphold the value of fairness',
      'We make a difference through professional and reliable legal representation',
      'We accompany our clients with steady steps towards the optimal solution',
      'Legally... you are not alone, we are with you every step of the way'
    ]
  };

  // Auto-advance slides every 7 seconds
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % backgroundSlides?.length);
    }, 7000);

    return () => clearInterval(timer);
  }, []);

  // Parallax scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <section data-component-id="src/app/landing-page/components/HeroSection.tsx:55:4" data-component-path="src/app/landing-page/components/HeroSection.tsx" data-component-line="55" data-component-end-line="107" data-component-file="HeroSection.tsx" data-component-name="section" data-component-content="%7B%22elementName%22%3A%22section%22%2C%22id%22%3A%22home%22%2C%22className%22%3A%22relative%20min-h-screen%20flex%20items-center%20justify-center%20overflow-hidden%22%7D"
      id="home"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      dir={isRTL ? 'rtl' : 'ltr'}
      style={{ paddingTop: '120px' }}
    >
      {/* Animated Background Slider with Parallax & Zoom */}
      <div data-component-id="src/app/landing-page/components/HeroSection.tsx:62:6" data-component-path="src/app/landing-page/components/HeroSection.tsx" data-component-line="62" data-component-end-line="94" data-component-file="HeroSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20inset-0%20w-full%20h-full%22%7D"
        className="absolute inset-0 w-full h-full"
        style={{
          transform: `translateY(${scrollY * 0.3}px)`
        }}
      >
        {backgroundSlides?.map((image, index) => (
          <div data-component-id="src/app/landing-page/components/HeroSection.tsx:69:10" data-component-path="src/app/landing-page/components/HeroSection.tsx" data-component-line="69" data-component-end-line="86" data-component-file="HeroSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
            key={index}
            className={`absolute inset-0 transition-opacity duration-1500 ${
              currentSlide === index ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <div data-component-id="src/app/landing-page/components/HeroSection.tsx:75:12" data-component-path="src/app/landing-page/components/HeroSection.tsx" data-component-line="75" data-component-end-line="82" data-component-file="HeroSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20inset-0%20bg-cover%20bg-center%22%7D"
              className="absolute inset-0 bg-cover bg-center"
              style={{
                backgroundImage: `url('${image}')`,
                transform: currentSlide === index ? 'scale(1.1)' : 'scale(1)',
                transition: 'transform 25s ease-in-out'
              }}
            />

            {/* Gradient overlay with brand colors */}
            <div data-component-id="src/app/landing-page/components/HeroSection.tsx:85:12" data-component-path="src/app/landing-page/components/HeroSection.tsx" data-component-line="85" data-component-end-line="85" data-component-file="HeroSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20inset-0%20bg-gradient-to-br%20from-primary%2F85%20via-primary%2F80%20to-accent%2F25%22%7D" className="absolute inset-0 bg-gradient-to-br from-primary/85 via-primary/80 to-accent/25" />
          </div>
        ))}

        {/* Soft Floating Elements */}
        <div data-component-id="src/app/landing-page/components/HeroSection.tsx:90:8" data-component-path="src/app/landing-page/components/HeroSection.tsx" data-component-line="90" data-component-end-line="93" data-component-file="HeroSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20inset-0%20overflow-hidden%20pointer-events-none%22%7D" className="absolute inset-0 overflow-hidden pointer-events-none">
          <div data-component-id="src/app/landing-page/components/HeroSection.tsx:91:10" data-component-path="src/app/landing-page/components/HeroSection.tsx" data-component-line="91" data-component-end-line="91" data-component-file="HeroSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20w-72%20h-72%20bg-accent%2F8%20rounded-full%20blur-3xl%20animate-float%20top-1%2F4%20left-1%2F4%22%7D" className="absolute w-72 h-72 bg-accent/8 rounded-full blur-3xl animate-float top-1/4 left-1/4" />
          <div data-component-id="src/app/landing-page/components/HeroSection.tsx:92:10" data-component-path="src/app/landing-page/components/HeroSection.tsx" data-component-line="92" data-component-end-line="92" data-component-file="HeroSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20w-96%20h-96%20bg-primary%2F15%20rounded-full%20blur-3xl%20animate-float-delayed%20top-1%2F2%20right-1%2F4%22%7D" className="absolute w-96 h-96 bg-primary/15 rounded-full blur-3xl animate-float-delayed top-1/2 right-1/4" />
        </div>
      </div>
      {/* Hero Content - Motivational Caption */}
      <div data-component-id="src/app/landing-page/components/HeroSection.tsx:96:6" data-component-path="src/app/landing-page/components/HeroSection.tsx" data-component-line="96" data-component-end-line="106" data-component-file="HeroSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22relative%20z-10%20container%20mx-auto%20px-4%20sm%3Apx-6%20lg%3Apx-8%22%7D" className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8">
        <div data-component-id="src/app/landing-page/components/HeroSection.tsx:97:8" data-component-path="src/app/landing-page/components/HeroSection.tsx" data-component-line="97" data-component-end-line="105" data-component-file="HeroSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-4xl%20mx-auto%20text-center%22%7D" className="max-w-4xl mx-auto text-center">
          {/* Main Motivational Caption with smooth fade */}
          <h1 data-component-id="src/app/landing-page/components/HeroSection.tsx:99:10" data-component-path="src/app/landing-page/components/HeroSection.tsx" data-component-line="99" data-component-end-line="104" data-component-file="HeroSection.tsx" data-component-name="h1" data-component-content="%7B%22elementName%22%3A%22h1%22%2C%22className%22%3A%22font-playfair%20font-bold%20text-3xl%20md%3Atext-5xl%20lg%3Atext-6xl%20text-white%20mb-8%20leading-tight%20animate-fade-in-up%20drop-shadow-lg%22%7D" className="font-playfair font-bold text-3xl md:text-5xl lg:text-6xl text-white mb-8 leading-tight animate-fade-in-up drop-shadow-lg">
            {language === 'ar' 
              ? motivationalCaptions?.ar?.[currentSlide % motivationalCaptions?.ar?.length]
              : motivationalCaptions?.en?.[currentSlide % motivationalCaptions?.en?.length]
            }
          </h1>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;